import { useTranslation } from "react-i18next";
import { NoReferenceIngredient } from "./NoReferenceIngredient";
import { useIngredientContext } from "../useIngredientContext";
import { Loading } from "src/components/utils/Loading";
import { ScoresScatterPlot } from "src/components/ScoresScatterPlot/ScoresScatterPlot";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useNavigate } from "react-router";
import { IngredientSupplierVisualisationChoice } from "../../../../components/ScoresScatterPlot/ScoreScatterPlot/ScoreScatterPlot";

export const MultipleSupplierScores = () => {
    const { t } = useTranslation();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const navigate = useNavigate();

    const {
        ingredient,
        scores,
        areScoresRefetching,
        suppliersScores,
        areSuppliersScoresRefetching,
        ingredientReference,
        isIngredientReferenceReady,
        ssqVersion,
        isSsqVersionReady,
        posts,
    } = useIngredientContext();

    if (!ingredient) {
        return <Loading />;
    }

    if (isIngredientReferenceReady && !ingredientReference) return <NoReferenceIngredient ingredient={ingredient} />;

    const atLeastOnePostLocked = posts?.some((post) => post.isLocked === true);

    return (
        <ScoresScatterPlot
            entity={ingredient}
            scores={scores}
            areScoresRefetching={areScoresRefetching}
            scatteredScores={suppliersScores}
            areScatteredScoresRefetching={areSuppliersScoresRefetching}
            scatteredScoresDimension="suppliers"
            getActivityDataValueByCompanyId={(
                companyId: number,
                visualisationChoiceKey: IngredientSupplierVisualisationChoice["key"],
            ): number => {
                if (!currentCampaign) return 0;

                const company = ingredient?.suppliers?.find(
                    (ingredientSupplier) => ingredientSupplier.supplier.id === companyId,
                );

                const activityDatas =
                    company?.ingredientSupplierActivityDatas?.filter(
                        (activityData) => activityData.campaign?.id === currentCampaign.id,
                    ) ?? [];

                return activityDatas.reduce((acc, activityData) => {
                    return acc + (activityData[visualisationChoiceKey] || 0);
                }, 0);
            }}
            ssqVersion={ssqVersion}
            isSsqVersionReady={isSsqVersionReady}
            posts={posts}
            visualisationChoices={[
                { key: "quantity", unit: "T", label: t("ingredient.activityform.quantity") },
                { key: "grossSales", unit: "€", label: t("ca_pour_cet_ingredient") },
            ]}
            textOptions={{
                dataLabel: t("fournisseurs", "Suppliers"),
                noScoresTitle: t(
                    "les_scores_indicateurs_de_l_ingredient_ne_peuvent_pas_etre_calcules_car_les_questionnaires_sont_vides",
                ),
                noScoresContent: t(
                    "envoyer_un_rappel_aux_contributeurs_des_questionnaires_pour_obtenir_les_donnees_necessaires_aux_calculs_de_ces_indicateurs",
                ),
            }}
            onScoreDistributionClick={(scoreId: number) => {
                if (!selectedCompany) return;

                const url = URLHandler.ingredient.scoreDistribution(
                    selectedCompany.id,
                    ingredient.id,
                    scoreId.toString(),
                );
                navigate(url);
            }}
            referenceScores={ingredient?.jsonAttributes?.["referenceScores"]}
            atLeastOnePostLocked={atLeastOnePostLocked}
        />
    );
};
